import {
  CheckOutlined,
  CloseOutlined,
  DeleteOutlined,
  EditOutlined,
} from '@ant-design/icons';
import { Button, Col, Popconfirm, Row, TableColumnType } from 'antd';
import { OfficeOutput } from 'gql/types';
import { ControlledInput, FormItem } from 'modules/common/components';
import { IconTooltip } from 'modules/common/components/IconTooltip';
import RegionSelect from 'modules/locations/components/RegionSelect';
import { Control, FormState } from 'react-hook-form';
import { getTranslation } from 'utils/getTranslation';

import { IEditOfficeFormSchema } from './schema';
import styles from './styles.module.scss';

const t = getTranslation('user.LocationsTable');

type FormErrorsType = FormState<IEditOfficeFormSchema>['errors'];
type ParamType = {
  editableKey?: string;
  setEditableKey?: (
    editableKey?: string,
    officeName?: string,
    idexxAccountNumber?: string,
    zoetisAccountNumber?: string,
    regionId?: string
  ) => void;
  onSaveChanges?: () => void;
  onDelete?: (officeId: string) => void;
  control: Control<IEditOfficeFormSchema, any>;
  savingIsDisabled?: boolean;
  isAllowedToEdit?: boolean;
  isAllowedToDelete?: boolean;
  errors: FormErrorsType;
  isMobile: boolean;
};

export const getColumns = (
  params: ParamType
): TableColumnType<OfficeOutput>[] => {
  const {
    control,
    setEditableKey,
    editableKey,
    onDelete,
    onSaveChanges,
    savingIsDisabled,
    isAllowedToDelete,
    isAllowedToEdit,
    errors,
  } = params;

  const isEditing = (officeId: string) => editableKey === officeId;
  const getNameErrorMessage = (errors: FormErrorsType) => errors.name?.message;
  const getIdexxErrorMessage = (errors: FormErrorsType) =>
    errors.idexxAccountNumber?.message;
  const getZoetisErrorMessage = (errors: FormErrorsType) =>
    errors.zoetisAccountNumber?.message;
  const getRegionErrorMessage = (errors: FormErrorsType) =>
    errors.regionId?.message;

  return [
    {
      width: '20%',
      title: t('name'),
      render: ({ id, name }: OfficeOutput) => {
        const errorMessage = getNameErrorMessage(errors);
        const validateStatus = errorMessage && 'error';
        return !isEditing(id) ? (
          <span title={name} className={styles.locationName}>
            {name}
          </span>
        ) : (
          <FormItem validateStatus={validateStatus} className={styles.formItem}>
            <ControlledInput
              placeholder={t('enterOfficeName')}
              suffix={
                <IconTooltip
                  text={errorMessage}
                  hidden={!errorMessage}
                  type="error"
                />
              }
              controlled={{ control, name: 'name', defaultValue: name }}
            />
          </FormItem>
        );
      },
    },
    {
      width: '20%',
      title: t('idexxAccountNumber'),
      render: ({ id, idexxAccountNumber }: OfficeOutput) => {
        const errorMessage = getIdexxErrorMessage(errors);
        const validateStatus = errorMessage && 'error';
        return !isEditing(id) ? (
          <span
            title={idexxAccountNumber || ''}
            className={styles.locationName}
          >
            {idexxAccountNumber}
          </span>
        ) : (
          <FormItem
            extra={errorMessage}
            validateStatus={validateStatus}
            className={styles.formItem}
          >
            <ControlledInput
              placeholder={t('enterIdexxAccountNumber')}
              suffix={
                <IconTooltip
                  text={errorMessage}
                  hidden={!errorMessage}
                  type="error"
                />
              }
              controlled={{
                control,
                name: 'idexxAccountNumber',
                defaultValue: idexxAccountNumber,
              }}
            />
          </FormItem>
        );
      },
    },
    {
      width: '20%',
      title: t('zoetisAccountNumber'),
      render: ({ id, zoetisAccountNumber }: OfficeOutput) => {
        const errorMessage = getZoetisErrorMessage(errors);
        const validateStatus = errorMessage && 'error';
        return !isEditing(id) ? (
          <span
            title={zoetisAccountNumber || ''}
            className={styles.locationName}
          >
            {zoetisAccountNumber}
          </span>
        ) : (
          <FormItem
            extra={errorMessage}
            validateStatus={validateStatus}
            className={styles.formItem}
          >
            <ControlledInput
              placeholder={t('enterZoetisAccountNumber')}
              suffix={
                <IconTooltip
                  text={errorMessage}
                  hidden={!errorMessage}
                  type="error"
                />
              }
              controlled={{
                control,
                name: 'zoetisAccountNumber',
                defaultValue: zoetisAccountNumber,
              }}
            />
          </FormItem>
        );
      },
    },
    {
      width: '20%',
      title: t('region'),
      render: ({ id, region }: OfficeOutput) => {
        const errorMessage = getRegionErrorMessage(errors);
        const validateStatus = errorMessage && 'error';
        return !isEditing(id) ? (
          <span title={region?.name || ''} className={styles.locationName}>
            {region?.name}
          </span>
        ) : (
          <FormItem
            extra={errorMessage}
            validateStatus={validateStatus}
            className={styles.formItem}
          >
            <RegionSelect
              controlled={{
                control,
                name: 'regionId',
                defaultValue: region?.id,
              }}
            />
          </FormItem>
        );
      },
    },
    {
      width: '20%',
      render: (officeOutput: OfficeOutput) => {
        return isEditing(officeOutput.id) ? (
          <Row gutter={15}>
            <Col span={12}>
              <Popconfirm
                overlayClassName={styles.popConfirm}
                getPopupContainer={(triggerNode) =>
                  triggerNode.parentNode as HTMLElement
                }
                title={t('sureToSave')}
                onConfirm={onSaveChanges}
                disabled={savingIsDisabled}
              >
                <Button type="text" size="small" disabled={savingIsDisabled}>
                  <CheckOutlined />
                </Button>
              </Popconfirm>
            </Col>
            <Col span={12}>
              <Button
                onClick={() => setEditableKey?.(undefined)}
                type="text"
                size="small"
              >
                <CloseOutlined />
              </Button>
            </Col>
          </Row>
        ) : (
          <Row align="middle">
            <Col span={12}>
              <Button
                onClick={() =>
                  setEditableKey?.(
                    officeOutput.id,
                    officeOutput.name,
                    officeOutput.idexxAccountNumber as string,
                    officeOutput.zoetisAccountNumber as string,
                    officeOutput.region?.id
                  )
                }
                type="link"
                size="small"
                disabled={!isAllowedToEdit}
              >
                <EditOutlined />
              </Button>
            </Col>
            <Col span={12}>
              <Popconfirm
                overlayClassName={styles.popConfirm}
                getPopupContainer={(triggerNode) =>
                  triggerNode.parentNode as HTMLElement
                }
                title={t('sureToDelete')}
                onConfirm={() => onDelete?.(officeOutput.id)}
                disabled={!isAllowedToDelete}
              >
                <Button danger type="link" disabled={!isAllowedToDelete}>
                  <DeleteOutlined />
                </Button>
              </Popconfirm>
            </Col>
          </Row>
        );
      },
    },
  ];
};
